import { useState, memo } from "react";
import {
  Grid,
  TextField,
  Button,
  Stack,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const Woocommerce = ({ ecommerce, warehouses, onSave, loading, isEdit }) => {
  const [formData, setFormData] = useState(ecommerce);
  const [deliveryInput, setDeliveryInput] = useState("");
  const [copiedTooltip, setCopiedTooltip] = useState("");

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        authentication: {
          ...prev.configuration.authentication,
          [field]: value,
        },
      },
    }));
  };

  const handleWarehouseChange = (e) => {
    setFormData({ ...formData, warehouse: e.target.value });
  };

  const addDeliveryMethod = () => {
    if (!deliveryInput) return;
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods || [];
    if (!currentMethods.includes(deliveryInput)) {
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          options: {
            ...prev.configuration.options,
            enabled_delivery_methods: [...currentMethods, deliveryInput],
          },
        },
      }));
    }
    setDeliveryInput("");
  };

  const removeDeliveryMethod = (method) => {
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods.filter(
        (m) => m !== method,
      );
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        options: {
          ...prev.configuration.options,
          enabled_delivery_methods: currentMethods,
        },
      },
    }));
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTooltip(fieldName);
      setTimeout(() => setCopiedTooltip(""), 2000);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form>
      <h3 style={{ marginBottom: "20px" }}>Configuración WooCommerce</h3>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Bodega Asociada</InputLabel>
        <Select
          value={formData.warehouse || ""}
          label="Bodega Asociada"
          onChange={handleWarehouseChange}
        >
          {warehouses.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
            Credenciales API
          </Typography>
          <TextField
            fullWidth
            label="Usuario"
            id="woocommerce_user"
            name="woocommerce_user"
            value={formData.configuration.authentication.woocommerce_user || ""}
            onChange={(e) => handleChange("woocommerce_user", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Contraseña"
            id="woocommerce_password"
            name="woocommerce_password"
            type="text"
            value={
              formData.configuration.authentication.woocommerce_password || ""
            }
            onChange={(e) =>
              handleChange("woocommerce_password", e.target.value)
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Token"
            id="token"
            name="token"
            value={formData.configuration.authentication.token || ""}
            onChange={(e) => handleChange("token", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Refresh Token"
            id="refresh_token"
            name="refresh_token"
            value={formData.configuration.authentication.refresh_token || ""}
            onChange={(e) => handleChange("refresh_token", e.target.value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
        Webhooks
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Webhook Crear Orden"
            value={formData.configuration.create_order?.url || ""}
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copiedTooltip === "create" ? "¡Copiado!" : "Copiar"}
                    arrow
                    open={copiedTooltip === "create" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.create_order?.url,
                          "create",
                        )
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Webhook Crear Orden con Plugin Ninja"
            value={formData.configuration.create_order_plugin?.url || ""}
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copiedTooltip === "plugin" ? "¡Copiado!" : "Copiar"}
                    arrow
                    open={copiedTooltip === "plugin" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.create_order_plugin?.url,
                          "plugin",
                        )
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Webhook Cancelar Orden"
            value={formData.configuration.cancel_order?.url || ""}
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copiedTooltip === "cancel" ? "¡Copiado!" : "Copiar"}
                    arrow
                    open={copiedTooltip === "cancel" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.cancel_order?.url,
                          "cancel",
                        )
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Webhook Actualizar Orden"
            value={formData.configuration.update_order?.url || ""}
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copiedTooltip === "update" ? "¡Copiado!" : "Copiar"}
                    arrow
                    open={copiedTooltip === "update" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.update_order?.url,
                          "update",
                        )
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 4,
          mb: 3,
          border: "1px solid #e0e0e0",
          p: 2,
          borderRadius: 2,
        }}
      >
        <InputLabel sx={{ mb: 1 }}>Métodos de Envío Habilitados</InputLabel>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Ej: Starken, Chilexpress..."
            value={deliveryInput}
            onChange={(e) => setDeliveryInput(e.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={addDeliveryMethod}
            disabled={!deliveryInput}
            sx={{ marginTop: "30px !important" }}
          >
            Agregar
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {formData.configuration.options.enabled_delivery_methods?.length >
          0 ? (
            formData.configuration.options.enabled_delivery_methods.map((m) => (
              <Chip
                key={m}
                label={m}
                onDelete={() => removeDeliveryMethod(m)}
                color="primary"
                variant="outlined"
              />
            ))
          ) : (
            <span style={{ color: "#999", fontSize: "0.9rem" }}>
              No hay métodos configurados
            </span>
          )}
        </Stack>
      </Box>

      <Button
        type="submit"
        variant="outlined"
        fullWidth
        disabled={loading}
        size="large"
        onClick={handleSubmit}
      >
        {isEdit ? "Guardar cambios de configuración" : "Finalizar integración"}
      </Button>
    </form>
  );
};

export default memo(Woocommerce);
